import { useEffect, useState } from "react";

import axios from "axios";
import { ChevronLeft, ChevronRight, Download, FileText, Menu, X } from "lucide-react";

import { getFileUrl } from "../../../lib/api";
import { cn } from "../../../lib/utils";

const isIOSSafariBrowser = () => {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isWebKit = /WebKit/i.test(ua);
  const isSafari = /Safari/i.test(ua);
  const isOtherBrowser = /CriOS|FxiOS|OPiOS|EdgiOS|Chrome|Chromium|Edg/i.test(ua);

  return isIOS && isWebKit && isSafari && !isOtherBrowser;
};

const isDesktopSafariBrowser = () => {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent || "";
  const isMacDesktop = /Macintosh|Mac OS X/i.test(ua) && !(navigator.maxTouchPoints > 1);
  const isWebKit = /WebKit/i.test(ua);
  const isSafari = /Safari/i.test(ua);
  const isOtherBrowser = /CriOS|FxiOS|OPiOS|EdgiOS|Chrome|Chromium|Edg/i.test(ua);

  return isMacDesktop && isWebKit && isSafari && !isOtherBrowser;
};

const DocumentViewer = ({ isOpen, onClose, data }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalBlobUrl, setModalBlobUrl] = useState("");
  const [mimeType, setMimeType] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [pdfOpenUrl, setPdfOpenUrl] = useState("");

  const isIOSSafari = isIOSSafariBrowser();
  const isDesktopSafari = isDesktopSafariBrowser();

  const dataArray = Array.isArray(data) ? data : data ? [data] : [];
  const currentItem = dataArray[currentIndex];

  useEffect(() => {
    let objectUrl = null;
    let isCancelled = false;

    const inferMimeTypeFromPath = (value) => {
      if (!value || typeof value !== "string") return "";

      const cleanValue = value.split("?")[0].toLowerCase();
      if (cleanValue.endsWith(".pdf")) return "application/pdf";
      if (cleanValue.endsWith(".jpg") || cleanValue.endsWith(".jpeg")) return "image/jpeg";
      if (cleanValue.endsWith(".png")) return "image/png";
      if (cleanValue.endsWith(".gif")) return "image/gif";
      if (cleanValue.endsWith(".webp")) return "image/webp";

      return "";
    };

    const blobToDataUrl = (blob) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result || "");
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

    const loadData = async () => {
      if (!isOpen || !currentItem) {
        setModalBlobUrl("");
        setMimeType("");
        setPdfOpenUrl("");
        return;
      }

      setLoading(true);
      try {
        if (typeof currentItem === "string" && currentItem.startsWith("data:")) {
          // Handle Base64
          const match = currentItem.match(/^data:([\w\-\.\/]+);base64,/);
          const type = match ? match[1] : "";
          setMimeType(type);

          if (type === "application/pdf") {
            const base64 = currentItem.split(",")[1];
            const byteCharacters = atob(base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/pdf" });
            objectUrl = URL.createObjectURL(blob);
            if (!isCancelled) {
              setModalBlobUrl(isIOSSafari || isDesktopSafari ? currentItem : objectUrl);
              setPdfOpenUrl(objectUrl);
            }
          } else {
            if (!isCancelled) {
              setModalBlobUrl(currentItem);
              setPdfOpenUrl("");
            }
          }
        } else if (typeof currentItem === "string" && !currentItem.startsWith("data:")) {
          // Handle S3/Local proxy retrieval
          const token = localStorage.getItem("token");
          const url = getFileUrl(currentItem);
          const response = await axios.get(url, {
            headers: {
              Authorization: token ? `Bearer ${token}` : ""
            },
            responseType: "blob"
          });

          const type = response.data.type || response.headers?.["content-type"] || inferMimeTypeFromPath(currentItem);
          if (!isCancelled) {
            setMimeType(type || "");
          }

          const responseBlob = response.data;
          const normalizedBlob =
            type === "application/pdf" && responseBlob?.type !== "application/pdf"
              ? new Blob([responseBlob], { type: "application/pdf" })
              : responseBlob;

          objectUrl = URL.createObjectURL(normalizedBlob);

          if (type === "application/pdf" && (isIOSSafari || isDesktopSafari)) {
            const pdfDataUrl = await blobToDataUrl(normalizedBlob);
            if (!isCancelled) {
              setModalBlobUrl(typeof pdfDataUrl === "string" ? pdfDataUrl : objectUrl);
              setPdfOpenUrl(objectUrl);
            }
          } else if (!isCancelled) {
            setModalBlobUrl(objectUrl);
            setPdfOpenUrl(type === "application/pdf" ? objectUrl : "");
          }
        }
      } catch (e) {
        console.error("Failed to load data in viewer:", e);
        if (!isCancelled) {
          setModalBlobUrl("");
          setPdfOpenUrl("");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isCancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isOpen, currentItem, isIOSSafari, isDesktopSafari]);

  useEffect(() => {
    if (isOpen) setCurrentIndex(0);
  }, [isOpen, data]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed top-0 left-0 w-full h-full bg-slate-900/60 dark:bg-black/80 backdrop-blur-md z-[100] flex overflow-hidden animate-in fade-in duration-300"
      onClick={onClose}
    >
      {/* Sidebar / Drawer */}
      {dataArray.length > 1 && (
        <>
          {/* Mobile Overlay */}
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[110] md:hidden animate-in fade-in duration-300"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}

          <div
            className={cn(
              "fixed inset-y-0 left-0 z-[120] w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col overflow-y-auto transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:z-0",
              isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <h3 className="text-yale-blue-900 dark:text-white text-xs font-bold uppercase tracking-wider">
                Attachments ({dataArray.length})
              </h3>
              <button
                className="md:hidden p-2 -mr-2 text-slate-400 hover:text-yale-blue-600 transition-colors"
                onClick={() => setIsSidebarOpen(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 p-3 space-y-2">
              {dataArray.map((item, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentIndex(index);
                    setIsSidebarOpen(false);
                  }}
                  className={cn(
                    "w-full p-3 rounded-xl transition-all flex items-center gap-4 text-left group",
                    currentIndex === index
                      ? "bg-yale-blue-600 text-white shadow-lg shadow-yale-blue-600/20"
                      : "hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-400"
                  )}
                >
                  <div
                    className={cn(
                      "w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors",
                      currentIndex === index
                        ? "bg-white/20"
                        : "bg-slate-100 dark:bg-slate-800 group-hover:bg-white dark:group-hover:bg-slate-700"
                    )}
                  >
                    <FileText
                      className={cn(
                        "w-5 h-5",
                        currentIndex === index ? "text-white" : "text-yale-blue-600 dark:text-yale-blue-400"
                      )}
                    />
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-xs font-semibold truncate mb-0.5">Document {index + 1}</p>
                    <p className={cn("text-[10px] truncate", currentIndex === index ? "text-yale-blue-100" : "text-slate-400")}>
                      {mimeType && currentIndex === index ? mimeType.split("/")[1].toUpperCase() : "Attachment"}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <div
        className="relative flex-1 flex flex-col overflow-hidden animate-in zoom-in-95 duration-500"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-16 md:h-20 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md flex items-center justify-between px-4 md:px-8 shrink-0">
          <div className="flex items-center gap-3">
            {dataArray.length > 1 && (
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="md:hidden w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 flex items-center justify-center active:scale-95 transition-all"
              >
                <Menu className="w-5 h-5" />
              </button>
            )}
            <div className="flex flex-col">
              <h2 className="text-yale-blue-900 dark:text-white font-bold text-sm md:text-lg leading-tight">
                {dataArray.length > 1 ? `Document ${currentIndex + 1}/${dataArray.length}` : "Document Preview"}
              </h2>
              {dataArray.length > 1 && (
                <p className="text-[10px] md:text-xs text-slate-500 dark:text-slate-400 font-medium hidden sm:block">
                  Switch between attachments
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {dataArray.length > 1 && (
              <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-xl p-1 mr-2">
                <button
                  onClick={() => setCurrentIndex((prev) => (prev > 0 ? prev - 1 : dataArray.length - 1))}
                  className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 hover:text-yale-blue-600 dark:hover:text-yale-blue-400 transition-all active:scale-90"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="w-[1px] h-4 bg-slate-200 dark:bg-slate-700 mx-0.5" />
                <button
                  onClick={() => setCurrentIndex((prev) => (prev < dataArray.length - 1 ? prev + 1 : 0))}
                  className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 hover:text-yale-blue-600 dark:hover:text-yale-blue-400 transition-all active:scale-90"
                  aria-label="Next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
            <button
              onClick={onClose}
              className="group w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-slate-100 dark:bg-slate-800 hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 flex items-center justify-center transition-all duration-300 active:scale-90"
            >
              <X className="w-5 h-5 md:w-6 md:h-6 transition-transform group-hover:rotate-90" />
            </button>
          </div>
        </div>

        <div className="flex-1 relative overflow-hidden bg-slate-50 dark:bg-slate-950/50 flex items-center justify-center p-4 md:p-10">
          <div className="w-full h-full overflow-auto flex items-center justify-center">
            {loading ? (
              <div className="flex flex-col items-center gap-6 animate-in fade-in duration-300">
                <div className="relative w-16 h-16">
                  <div className="absolute inset-0 rounded-full border-4 border-yale-blue-500/20"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-t-yale-blue-500 animate-spin"></div>
                </div>
                <div className="text-center">
                  <p className="text-yale-blue-900 dark:text-white font-bold mb-1">Loading Content</p>
                  <p className="text-slate-500 dark:text-slate-400 text-xs">Preparing document for high-quality preview...</p>
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {(() => {
                  if (mimeType.startsWith("image/")) {
                    return (
                      <img
                        src={modalBlobUrl}
                        alt="Full View"
                        className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 animate-in zoom-in-95 duration-500"
                      />
                    );
                  } else if (mimeType === "application/pdf" && modalBlobUrl) {
                    return (
                      <div className="w-full h-full flex flex-col gap-3">
                        {isIOSSafari && (
                          <div className="flex justify-end">
                            <a
                              href={pdfOpenUrl || modalBlobUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-yale-blue-600 text-white text-xs font-semibold hover:bg-yale-blue-700 transition-colors"
                            >
                              <Download className="w-4 h-4" />
                              Open PDF
                            </a>
                          </div>
                        )}
                        <embed
                          src={modalBlobUrl}
                          type="application/pdf"
                          className="w-full h-full min-h-[60vh] rounded-xl bg-white"
                        />
                      </div>
                    );
                  } else {
                    return (
                      <div className="max-w-md w-full p-10 text-center bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl animate-in slide-in-from-bottom-8 duration-500">
                        <div className="w-24 h-24 bg-yale-blue-500/10 dark:bg-yale-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-8 ring-8 ring-yale-blue-500/5">
                          <FileText className="w-10 h-10 text-yale-blue-600 dark:text-yale-blue-400" />
                        </div>
                        <h3 className="text-xl font-bold text-yale-blue-900 dark:text-white mb-3">No Preview Available</h3>
                        <p className="mb-10 text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                          This file format cannot be previewed directly in the browser. You can download it to view locally.
                        </p>
                        <a
                          href={modalBlobUrl || currentItem}
                          download
                          className="inline-flex items-center justify-center w-full px-8 py-4 bg-yale-blue-600 text-white font-bold rounded-2xl hover:bg-yale-blue-700 transition-all shadow-lg shadow-yale-blue-600/30 hover:shadow-yale-blue-600/40 active:scale-95 flex items-center gap-3 group"
                        >
                          <Download className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform" />
                          <span>Download Document</span>
                        </a>
                      </div>
                    );
                  }
                })()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
